import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import cx from 'clsx';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import styles from './index.module.scss';
import { CheckableFields, Checkbox, HelperTooltip, Input, RichTextEditor, Tips } from '..';
import { IconDone } from '@/assets/quest';
import { Control, Controller, FieldValues, useForm, useWatch } from 'react-hook-form';
import { QuestParticipation } from './components/ParticipationField';
import { RichTextEditorHandle } from '../RichTextEditor';
import { useCommentEligibility, useDebounce, useSwitch } from '@/hooks';
import { PostFooter } from '../PostFooter';
import { LuckyDrawCashPrize } from './components/LuckyDrawCashPrize';
import { TotalFeeCard, UnitQuestFeeCard } from './components/PayInfoCard';
import { ErrorMessage } from '@hookform/error-message';
import { queryClient } from '@/utils';
import { QueryClientProvider } from 'react-query';

import { Action, QuestType } from './type';
import { ActionFields } from './components/ActionFields';
import { checkActionsEmpty } from './utils';
import { useTotalQuestEstimate } from './components/PayInfoCard/hooks';

export const MinJoinCount = 1000;

export const MaxJoinCount = 10000;

const actionsFilter = (actions: Action[]) => {
  return actions.filter(i => i.type && i.data);
};

export type QuestProps = {
  onConfirm: (data: EditorSubmitContentData) => Promise<void>;
  disabled?: boolean;
  className?: string;
  language?: SUPPORTED_LOCALE_TYPE;
  refreshKey?: string;
  hiddenEligibilityFields?: string[];
  hideChannelPicker?: boolean;
  isAlpha?: boolean; // 方便测试
};

export type QuestHandle = {
  setLoading: (loading: boolean) => void;
};

export type QuestControlProps = Control<FieldValues, object>;

const DefaultActions = { type: undefined, data: undefined };

const QuestInner = forwardRef<QuestHandle, QuestProps>((props, ref) => {
  const {
    className,
    disabled,
    language,
    hiddenEligibilityFields,
    onConfirm,
    hideChannelPicker,
    isAlpha,
  } = props;
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isEditorInValid, turn: setInvalid } = useSwitch();
  const editorRef = useRef<RichTextEditorHandle>(null);
  useImperativeHandle(
    ref,
    () => ({
      setLoading: setLoading,
    }),
    [setLoading],
  );

  const { control, handleSubmit, formState, setValue } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      durationDay: '',
      actions: [DefaultActions],
      permissions: {},
      draw: null,
      estimated_join_count: null,
      is_private: false,
      enableLuckDraw: false,
    },
  });

  const [commentEligibility] = useCommentEligibility();

  const [actions, durationDay, draw, estimatedJoinCount, isPrivate, enableLuckDraw, permissions] =
    useWatch({
      control,
      name: [
        'actions',
        'durationDay',
        'draw',
        'estimated_join_count',
        'is_private',
        'enableLuckDraw',
        'permissions',
      ],
    });

  const validPermissions = useMemo(
    () =>
      Object.keys(permissions).reduce((m, n) => {
        if (Array.isArray(permissions[n])) {
          // 这里注意，处理的很糙，选中且输入有值
          if (permissions[n][0] && permissions[n][1]) {
            const num = Number(permissions[n][1]);
            m[n] = isNaN(num) ? permissions[n][1] : num;
          }
        } else if (permissions[n]) {
          m[n] = true;
        }
        return m;
      }, {} as any),
    [permissions],
  );

  const isActionEmpty = useMemo(() => checkActionsEmpty(actions), [actions]);

  const duration = useMemo(
    () => (durationDay ? Number(durationDay) * 24 * 3600 : 0),
    [durationDay],
  );

  const questData = useMemo(() => {
    const questData = {
      actions: actionsFilter(actions),
      permissions: validPermissions,
    };

    if (estimatedJoinCount) {
      Object.assign(questData, {
        estimated_join_count: Number(estimatedJoinCount),
      });
    }

    if (draw?.prize_count && draw?.prize_value && enableLuckDraw) {
      Object.assign(questData, {
        draw_data: {
          prize_count: Math.floor(draw?.prize_count),
          prize_value: Number(draw?.prize_value) * 10000,
        },
      });
    }

    if (isPrivate) {
      Object.assign(questData, {
        is_private: true,
      });
    }
    return questData;
  }, [
    actions,
    draw?.prize_count,
    draw?.prize_value,
    enableLuckDraw,
    estimatedJoinCount,
    isPrivate,
    validPermissions,
  ]);

  const [debounceQuestData] = useDebounce<QuestType>(questData, 500);

  const { data: estimateData, isLoading: isEstimateLoading } = useTotalQuestEstimate({
    // 查询总价需要全部quest数据
    quest: debounceQuestData,
  });

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        const params: EditorSubmitContentData = {
          type: 'quest',
          content: '',
          permissions: commentEligibility,
        };
        params.quest_data = {
          ...questData,
          name: data.name,
          duration: duration,
          permissions: validPermissions,
        };
        if (estimateData) {
          // 总价大于等于100美金需要code
          const needCode = estimateData.total_deposit >= 100;
          params.needCode = needCode;
        }
        const textData = await editorRef.current?.getSubmitContent();
        if (textData) {
          Object.assign(params, {
            content: textData.textContent,
            entities: {
              ...(textData.mentionsList?.length ? { mentions: textData.mentionsList } : {}),
              ...(textData.hashtagsList?.length ? { tags: textData.hashtagsList } : {}),
              ...(textData.urlsWidthIndices?.length ? { url: textData.urlsWidthIndices } : {}),
              ...(textData.slashesList.length ? { channels: textData.slashesList } : {}),
            },
          });
        }
        await onConfirm?.(params);
      } catch (error) {
        setLoading(false);
      }
    },
    [
      commentEligibility,
      duration,
      estimateData,
      onConfirm,
      questData,
      setLoading,
      validPermissions,
    ],
  );

  const minJoinCount = useMemo(
    () =>
      // alpha环境下不限制人数，方便测试
      isAlpha || isPrivate ? 1 : MinJoinCount,
    [isAlpha, isPrivate],
  );

  const isJoinCountValid = useMemo(
    () =>
      !!debounceQuestData.estimated_join_count &&
      debounceQuestData.estimated_join_count >= minJoinCount &&
      debounceQuestData.estimated_join_count <= MaxJoinCount,
    [debounceQuestData.estimated_join_count, minJoinCount],
  );

  useEffect(() => {
    setValue('estimated_join_count', null);
  }, [isPrivate, setValue]);

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.content}>
        <div className={styles.title}>Basic info</div>
        <div className={styles.filedContainer}>
          <div className={styles.subTitle}>Description</div>
          <RichTextEditor
            maxLength={600}
            editorClassName={styles.textEditor}
            placeholderClassName={styles.editorStyle}
            wrapperClassName={styles.editorWrapper}
            toolbar={false}
            footer={false}
            disableUploadImage
            placeholder={'I have something to describe...'}
            disabled={disabled}
            onInvalidChange={setInvalid}
            disableCard
            ref={editorRef}
            language={language}
          />
        </div>
        <div className={styles.filedContainer}>
          <div className={styles.subTitle}>Title</div>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                className={styles.titleInput}
                maxLength={30}
                placeholder="Enter quest title"
                showLength
                value={field.value}
                onChange={field.onChange}
                ref={field.ref}
              />
            )}
          />
        </div>
        <div className={styles.filedContainer}>
          <div className={styles.subTitle}>Quest period</div>
          <div className={styles.questPeriod}>
            <div className={styles.startTime}>
              <div className={styles.name}>Starting time</div>
              <div className={styles.value}>
                <HelperTooltip title="Adjustment is not currently supported.">
                  <IconDone className={styles.icon} />
                </HelperTooltip>
                Immediately start after posting.
              </div>
            </div>
            <div className={styles.duration}>
              <div className={styles.name}>Duration</div>
              <div className={styles.value}>
                <Controller
                  name="durationDay"
                  control={control}
                  rules={{
                    required: true,
                    min: { value: 1, message: 'Can be set to last for 1-30 days' },
                    max: { value: 30, message: 'Can be set to last for 1-30 days' },
                  }}
                  render={({ field, fieldState }) => (
                    <div className={styles.durationField}>
                      <div className={styles.durationInputContainer}>
                        <Input
                          className={styles.durationInput}
                          maxLength={30}
                          placeholder="1-30"
                          value={field.value}
                          onChange={field.onChange}
                          ref={field.ref}
                          type="number"
                          error={!!fieldState?.error}
                        />
                        <ErrorMessage
                          errors={formState.errors}
                          name="durationDay"
                          render={({ message }) => (
                            <div className={styles.errorMessage}>{message}</div>
                          )}
                        />
                      </div>
                      <span className={styles.durationSuffix}>day</span>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.filedContainer}>
          <div className={styles.subTitle}>
            Participation Requirements{' '}
            <span className={styles.optional}>
              (Optional. Selecting options to acquire more quality users will incur additional fee.)
            </span>
          </div>
          <CheckableFields
            className={styles.checkableFieldsContainer}
            control={control}
            fields={QuestParticipation}
          />
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.title}>Quest setting</div>
        <ActionFields control={control} quest={debounceQuestData} />
        {!isActionEmpty ? (
          <div className={styles.unitQuestFee}>
            <UnitQuestFeeCard quest={debounceQuestData} isActionEmpty={isActionEmpty} />
          </div>
        ) : null}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>
          Cash Lucky draw <span className={styles.optional}>(optional)</span>
          <Tips title="Users need to complete all actions to join the Lucky Draw." />
        </div>
        <div className={styles.luckyDrawSwitch}>
          <Controller
            name="enableLuckDraw"
            control={control}
            render={({ field }) => (
              <Checkbox
                type="form"
                isCircle
                checked={field.value}
                onChange={e => field.onChange(e.target.checked)}>
                Add Cash lucky draw to your quest.
              </Checkbox>
            )}
          />
          {enableLuckDraw && (
            <div className={styles.luckyDrawFields}>
              <LuckyDrawCashPrize control={control} isAlpha={isAlpha} />
            </div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.title}>Deposit Quest</div>
        <Controller
          name="is_private"
          control={control}
          render={({ field }) => {
            return (
              <div className={styles.questTypeSelect}>
                <Checkbox
                  type="form"
                  isCircle
                  checked={!field.value}
                  onChange={e => field.onChange(!e.target.checked)}>
                  Public
                </Checkbox>
                <Checkbox
                  type="form"
                  isCircle
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}>
                  Private
                  <Tips title="You can post in private and test your quest before releasing to public. The link of private quest will expire 30min after posting. Please change it to public status in time." />
                </Checkbox>
              </div>
            );
          }}
        />
        <div className={styles.subTitle}>Expected participants for quest</div>
        <div className={styles.joinCountContainer}>
          <Controller
            name="estimated_join_count"
            control={control}
            rules={{
              required: true,
              min: { value: minJoinCount, message: `At least ${minJoinCount}` },
              max: { value: MaxJoinCount, message: `Maximum value is ${MaxJoinCount}` },
            }}
            render={({ field, fieldState }) => (
              <div className={styles.joinCountInputContainer}>
                <div className={styles.joinCountInput}>
                  <Input
                    className={styles.input}
                    placeholder={isPrivate ? '' : `At least ${minJoinCount}`}
                    value={field.value}
                    onChange={field.onChange}
                    ref={field.ref}
                    disabled={isActionEmpty}
                    type="number"
                    error={!!fieldState?.error}
                  />
                  <Tips
                    title={
                      isActionEmpty
                        ? 'Set quest action first.'
                        : 'Users who complete one action will be considered as participants.'
                    }
                  />
                </div>
                <ErrorMessage
                  errors={formState.errors}
                  name="estimated_join_count"
                  render={({ message }) => <div className={styles.errorMessage}>{message}</div>}
                />
              </div>
            )}
          />
        </div>
        <TotalFeeCard
          questEstimate={estimateData}
          isLoading={isEstimateLoading}
          showValue={isJoinCountValid}
        />
      </div>
      <PostFooter
        className={styles.postFooter}
        disabled={
          disabled ||
          isEditorInValid ||
          isActionEmpty ||
          !formState.isValid ||
          !questData ||
          isEstimateLoading
        }
        isLoading={isLoading}
        onSubmit={handleSubmit(onSubmit)}
        hiddenEligibilityFields={hiddenEligibilityFields}
        hideChannelPicker={hideChannelPicker}
      />
    </div>
  );
});

export const Quest = forwardRef<QuestHandle, QuestProps>((props, ref) => {
  return (
    <QueryClientProvider client={queryClient}>
      <QuestInner {...props} ref={ref} />
    </QueryClientProvider>
  );
});
